import React from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  Contract,
  ContractModificationRequest,
  ContractTenant,
} from '../../../../common/validations'
import { ResidentAppError } from '../../../../common/error'
import { ResidentAppErrorCode } from '../../../../common/types'
import { sortGivenTenantFirst } from '../../../utils/sortGivenTenantFirst'
import { useAPIGetContractModificationRequestSuspense } from '../../../api/apiHooks/useAPIGetContractModificationRequest'

import { useAPIGetContractSuspense } from 'api/apiHooks/useAPIGetContract'
import { useAPIGetUserProfileSuspense } from 'api/apiHooks/useAPIGetUserProfile'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { useAPIGetContractTenantsSuspense } from 'api/apiHooks/useAPIGetContractTenants'
import { useAPIGetUserMetadataSuspense } from 'api/apiHooks/useAPIGetUserMetadata'

interface Data {
  contract: Contract
  tenants: ContractTenant[]
  contractModificationRequest: ContractModificationRequest
  userMetadata: { isImpersonator: boolean }
}

export const DataLoader = ({
  children,
}: {
  children: (data: Data) => React.ReactNode
}) => {
  const [params] = useSearchParams()
  const { currentContractId } = useCurrentContract()
  const { data: contractData } = useAPIGetContractSuspense(currentContractId)
  const { data: userData } = useAPIGetUserProfileSuspense()
  const { data: contractModificationRequestData } =
    useAPIGetContractModificationRequestSuspense(currentContractId)
  const { data: contractTenantsData } =
    useAPIGetContractTenantsSuspense(currentContractId)

  const { data: userMetadata } = useAPIGetUserMetadataSuspense()
  const currentUserName = `${userData.firstName} ${userData.lastName}`

  // Handle redirect error from the API
  const error = params.get('error')

  if (error) {
    throw ResidentAppError.ClientError(error as ResidentAppErrorCode)
  }

  return children({
    contract: contractData,
    tenants: sortGivenTenantFirst(currentUserName, contractTenantsData),
    contractModificationRequest: contractModificationRequestData,
    userMetadata,
  })
}
